import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useMemo } from "react"
import { useNavigate } from "react-router"
import type { Declaration } from "../../../../api/types"
import { DECLARATIONS_BASE } from "../../routes"
import { ReviewModeProvider } from "./reviewMode"
import { StudioV2Layout } from "./StudioV2Layout"
// The shared document-viewer / power-bar styles (`.doc-bar*`, `.doc-viewer`, tokens
// `studioV2.css` builds on). Imported here explicitly, before `studioV2.css` as
// before: on this page they used to arrive through the retired Studio V1 screens.
import "../studioScreenA.css"
import "./studioV2.css"
import { useStudioV2State } from "./useStudioV2State"

export interface StudioV2PageProps {
  declId: string
  supplierName: string
  /** The declarations list `DeclarationReviewPage` already fetched
   *  (`useDeclarationsWithPolling`) — threaded down rather than re-queried,
   *  so "Überspringen" and the post-approve success screen's "next
   *  declaration" read the SAME list the page used to find this one. */
  declarations: Declaration[]
  /** C2 — this declaration's own row, when the page opened from it
   *  (`MC_REVIEW_FAST_OPEN`); see `StudioV2Layout`'s prop of the same name. */
  declaration?: Declaration
  /** B5 — `DeclarationReviewPage`'s own `isAwaitingExtraction`, threaded
   *  straight through to `StudioV2Layout` (see that prop's own doc). */
  awaitingExtraction?: boolean
}

/**
 * The declaration review studio, rendered by DeclarationReviewPage.
 * Composes `useStudioV2State` and hands its result to the shell.
 *
 * LOADING is the shell's own business, per pane: the document pane
 * needs nothing but the declaration id, so it mounts and starts its byte fetch
 * at once, while the navigator, mask and footer hold skeletons until their
 * reads land (`StudioV2Layout`'s `loading`). A single page-wide skeleton here
 * used to hold the PDF back until the slowest review read (`read-model`, ~9 s
 * on a 489-article declaration) had returned, then only START the document
 * download. ERROR never dead-ends: alongside the notice it offers a way back
 * to the list and a retry, the same "never a dead end" contract the footer's
 * approve gate holds elsewhere in this feature.
 */
export function StudioV2Page({
  declId,
  supplierName,
  declarations,
  declaration,
  awaitingExtraction,
}: StudioV2PageProps) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const state = useStudioV2State(declId)
  const paged = state.paged === true
  const { activeFramework, pagedTables } = state
  // Memoised: every `useConditionRows` consumer (each substance row) reads it.
  const reviewMode = useMemo(
    () => ({ paged, activeFramework, pagedTables }),
    [paged, activeFramework, pagedTables],
  )

  if (state.isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-card px-6 text-center">
        <p className="text-[13px] text-grey5">
          {translate("MC_DECL_V2_LOAD_ERROR")}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(DECLARATIONS_BASE)}
            className="sv2-btn sv2-btn-sm sv2-btn-ghost"
          >
            {translate("MC_DECL_FAILED_BACK")}
          </button>
          {/* `useStudioV2State` exposes no `refetch` (its reads are joined
           *  into one memo, not individually re-triggerable from here) — a
           *  full reload is the honest retry available at this file's scope. */}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="sv2-btn sv2-btn-sm sv2-btn-primary"
          >
            {translate("MC_DECL_BTN_RETRY")}
          </button>
        </div>
      </div>
    )
  }

  // STEP C3-b2: the editor parts read the open regulation's slice when the page
  // reads one regulation at a time (fixed per mount inside `useStudioV2State`).
  // `key`: the only time the source can change is a fallback BEFORE the first paint;
  // remounting there guarantees no part ever calls a different set of hooks.
  return (
    <ReviewModeProvider value={reviewMode}>
      <StudioV2Layout
        key={state.paged ? "slices" : "full"}
        supplierName={supplierName}
        state={state}
        declarations={declarations}
        declaration={declaration}
        awaitingExtraction={awaitingExtraction}
      />
    </ReviewModeProvider>
  )
}
